import { useMemo, useState } from "react";
import { getAccountGroupsByUser, getGoalsByUser, getRoutinesByUser, getTradingSessionsByUser, useQuery } from "wasp/client/operations";
import useTranslation from "../../../client/hooks/useTranslation";
import TradingCalendar from "../../../dashboard/TradingCalendar";
import DayDetailModal from "../../../dashboard/DayDetailModal";
import { buildAccountEvents, buildFundedEvents, buildPayoutEvents, buildViolationsByDate } from "../../../dashboard/calendarEvents";
import { useLiteData } from "../../data/LiteDataProvider";

/**
 * Calendrier du tableau de bord Lite : LE calendrier de l'Advanced (TradingCalendar)
 * et sa fenêtre de détail du jour, alimentés par les filtres du Lite. Même charte,
 * mêmes informations (routines, objectifs, comptes, payouts, funded, violations,
 * jours fériés, erreurs de session), et un seul code à faire évoluer.
 */
export function LiteCalendar() {
  const t = useTranslation();
  const { scopedTrades, rawAccounts, accounts, inScope } = useLiteData();
  // Mêmes requêtes que le tableau de bord Advanced : react-query partage le cache.
  const { data: routines = [] } = useQuery(getRoutinesByUser);
  const { data: goals = [] } = useQuery(getGoalsByUser);
  const { data: groups = [] } = useQuery(getAccountGroupsByUser);
  const { data: sessions = [] } = useQuery(getTradingSessionsByUser);
  const [selectedDay, setSelectedDay] = useState<Date | null>(null);

  const trades = useMemo(() => scopedTrades.map(tr => tr.raw), [scopedTrades]);
  // Comptes retenus par les filtres compte / groupe / prop firm du Lite.
  const scopeAccounts = useMemo(() => {
    const ids = new Set(accounts.filter(a => inScope(a)).map(a => a.id));
    return rawAccounts.filter(a => ids.has(a.id)) as any[];
  }, [accounts, rawAccounts, inScope]);

  const accountEvents = useMemo(() => buildAccountEvents(scopeAccounts), [scopeAccounts]);
  const fundedEvents = useMemo(() => buildFundedEvents(scopeAccounts), [scopeAccounts]);
  const payoutEvents = useMemo(() => buildPayoutEvents(scopeAccounts), [scopeAccounts]);
  // Comme l'Advanced par défaut : violations des seuls comptes actifs.
  const violationsByDate = useMemo(
    () => buildViolationsByDate(scopeAccounts.filter(a => a.isActive !== false), t.dashboard.calendar.violations),
    [scopeAccounts, t],
  );

  return (
    <>
      <TradingCalendar
        className="h-full"
        variant="lite"
        title={t.lite.dashboard.calendar.title}
        subtitle={t.lite.dashboard.calendar.subtitle}
        trades={trades}
        routines={routines as any[]}
        goals={goals as any[]}
        accounts={rawAccounts as any[]}
        groups={groups as any[]}
        accountEvents={accountEvents}
        fundedEvents={fundedEvents}
        payoutEvents={payoutEvents}
        sessions={sessions as any[]}
        violationsByDate={violationsByDate}
        onDayClick={setSelectedDay}
      />
      <DayDetailModal
        date={selectedDay}
        open={selectedDay !== null}
        onOpenChange={open => { if (!open) setSelectedDay(null); }}
        trades={trades}
        sessions={sessions as any[]}
        routines={routines as any[]}
        goals={goals as any[]}
        accounts={rawAccounts as any[]}
        groups={groups as { id: string; name: string }[]}
        accountEvents={accountEvents}
        fundedEvents={fundedEvents}
        payoutEvents={payoutEvents}
        violationsByDate={violationsByDate}
        onDeleted={() => setSelectedDay(null)}
      />
    </>
  );
}
